/**
 * main.ts — circular-ruin composition root.
 *
 * Phase 4:
 *   Wires AudioEngine + VisualEngine + InputLayer + MachineMapper + SessionManager
 *   into the spec §5 rAF loop so a real iPhone can play the gesture-mapped
 *   feedback machine.
 *
 * Constraints:
 *   - Never imports DOM-touching libraries beyond what engines/UX wrap.
 *   - Audio context is captured from Tone; unmuteIOSAudio() + Tone.start() both
 *     start synchronously inside the first user gesture so iOS silent-switch +
 *     autoplay both unlock.
 */

import * as Tone from 'tone';

import { unmuteIOSAudio } from './util/unmute-ios-audio';
import { shouldOpenInstrumentGate } from './util/instrument-gate';
import { initBrandMenu } from './util/brand-menu';
import { loadModes } from './modes';
import { AudioEngine } from './components/AudioEngine';
import { VisualEngine } from './components/VisualEngine';
import { InputLayer } from './components/InputLayer';
import { MachineMapper } from './components/MachineMapper';
import { SessionManager } from './components/SessionManager';
import type {
  MachineMapperOutput,
  ModeConfig,
  GestureFrame,
  StateName,
  VisualParams,
  AudioLevels,
} from './types';

// ─── Constants ──────────────────────────────────────────────────────────────

const IDLE_EVOLVE_MS = 8000; // spec §7 row 10
const TEST_FAST =
  typeof location !== 'undefined' && new URLSearchParams(location.search).has('test-fast');
const IDLE_EVOLVE_MS_EFFECTIVE = TEST_FAST ? 800 : IDLE_EVOLVE_MS;

const DEBUG =
  typeof location !== 'undefined' && new URLSearchParams(location.search).has('debug');
const E2E =
  typeof location !== 'undefined' && new URLSearchParams(location.search).has('e2e');

// ─── DOM resolution ─────────────────────────────────────────────────────────

interface DomRefs {
  canvas: HTMLCanvasElement;
  splash: HTMLElement;
  tapStatus: HTMLElement;
  audioLoadBar: HTMLElement;
  silentAudio: HTMLAudioElement;
  hapticTap: HTMLLabelElement | null;
  hapticHard: HTMLLabelElement | null;
}

function resolveDom(): DomRefs {
  const canvas = document.getElementById('stage');
  const splash = document.getElementById('tap-to-start');
  const tapStatus = document.getElementById('tap-status');
  const audioLoadBar = document.getElementById('audio-load-bar');
  const silentAudio = document.getElementById('silent');
  if (!(canvas instanceof HTMLCanvasElement)) throw new Error('main: #stage canvas missing');
  if (!(splash instanceof HTMLElement)) throw new Error('main: #tap-to-start splash missing');
  if (!(tapStatus instanceof HTMLElement)) throw new Error('main: #tap-status missing');
  if (!(audioLoadBar instanceof HTMLElement)) throw new Error('main: #audio-load-bar missing');
  if (!(silentAudio instanceof HTMLAudioElement))
    throw new Error('main: #silent audio element missing');
  return {
    canvas,
    splash,
    tapStatus,
    audioLoadBar,
    silentAudio,
    hapticTap: document.getElementById('haptic-tap') as HTMLLabelElement | null,
    hapticHard: document.getElementById('haptic-hard') as HTMLLabelElement | null,
  };
}

// ─── Canvas sizing ──────────────────────────────────────────────────────────

interface CanvasRenderSize {
  width: number;
  height: number;
}

function getCanvasRenderSize(): CanvasRenderSize {
  // E2E mode: tiny fixed canvas. Headless Chromium mobile emulation reports
  // outrageous innerWidth/DPR combinations that produce 10000+ px canvases,
  // exceed GL_MAX_RENDERBUFFER_SIZE, and freeze the rAF loop. The E2E test
  // only cares about state transitions, not visual fidelity, so we use a small
  // predictable size.
  if (E2E) {
    return { width: 256, height: 256 };
  }
  // Let three.js own DPR via setPixelRatio(). This returns CSS-pixel render
  // dimensions, optionally downscaled so the final drawing buffer stays bounded.
  const MAX_SIDE = 2048;
  const rawDpr = window.devicePixelRatio ?? 1;
  const dpr = Math.min(Math.max(rawDpr, 1), 3);
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  const scale = Math.min(1, MAX_SIDE / Math.max(viewportW * dpr, viewportH * dpr));
  return {
    width: Math.max(1, Math.floor(viewportW * scale)),
    height: Math.max(1, Math.floor(viewportH * scale)),
  };
}

// ─── Haptics ────────────────────────────────────────────────────────────────

function fireHaptic(refs: DomRefs, flavor: 'tap' | 'hard'): void {
  const el = flavor === 'hard' ? refs.hapticHard : refs.hapticTap;
  el?.click();
}

// ─── Body state attribute ───────────────────────────────────────────────────

function reflectState(state: StateName, refs: DomRefs): void {
  document.body.dataset.state = state;
  if (state === 'AWAITING_TAP' || state === 'LOADING') {
    refs.splash.style.display = 'flex';
  } else {
    refs.splash.style.display = 'none';
  }
}

function setSplashStatus(
  refs: DomRefs,
  status: string,
  progress: number,
  disabled: boolean,
  ariaLabel: string,
): void {
  refs.tapStatus.textContent = status;
  refs.splash.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  refs.splash.setAttribute('aria-label', ariaLabel);
  const clampedProgress = Math.min(1, Math.max(0, progress));
  refs.audioLoadBar.style.transform = `scaleX(${clampedProgress.toFixed(3)})`;
}

// ─── Debug HUD ──────────────────────────────────────────────────────────────

/** Append-only diagnostics panel visible when ?debug=1. Updated each frame. */
function makeDebugHud(): HTMLDivElement {
  const el = document.createElement('div');
  el.id = 'debug-hud';
  el.style.cssText = [
    'position:fixed', 'top:8px', 'left:8px', 'z-index:100',
    'background:rgba(0,0,0,0.7)', 'color:#0f0', 'font:11px/1.4 monospace',
    'padding:8px', 'border:1px solid #0f0', 'border-radius:4px',
    'max-width:60vw', 'pointer-events:none', 'white-space:pre',
  ].join(';');
  el.textContent = 'debug hud: initialising…';
  document.body.appendChild(el);
  return el;
}

// ─── Activity peek ──────────────────────────────────────────────────────────

/** Cheap "did anything happen this frame" check derived from the flushed gesture. */
function gestureHadActivity(g: GestureFrame): boolean {
  return (
    g.fingers.size > 0 ||
    g.taps.length > 0 ||
    g.doubleTaps.length > 0 ||
    g.holds.length > 0 ||
    g.pinch !== null ||
    g.rotate !== null
  );
}

// ─── Boot ───────────────────────────────────────────────────────────────────

async function boot(): Promise<void> {
  const refs = resolveDom();
  const initialCanvasSize = getCanvasRenderSize();
  setSplashStatus(refs, 'LOADING AUDIO', 0.12, true, 'Loading audio');

  // Mobile hamburger drawer for the top nav. Pure DOM/CSS — wired here so
  // it's live the moment the page boots, regardless of audio unlock state.
  initBrandMenu();

  const hud = DEBUG ? makeDebugHud() : null;
  let frameCount = 0;
  let lastHudAtMs = 0;

  // 1. Modes — must load before VisualEngine (needs modes upfront).
  const modesFile = await loadModes('/media/modes.json');
  setSplashStatus(refs, 'LOADING AUDIO', 0.28, true, 'Loading audio');
  let activeModeIndex: 0 | 1 | 2 = 0;
  let activeMode: ModeConfig = modesFile.modes[0];

  // 2. Engines. AudioEngine loads its own modes via modesUrl; VisualEngine accepts
  //    the loaded array.
  const audioEngine = new AudioEngine();
  const visualEngine = new VisualEngine({
    canvas: refs.canvas,
    modes: modesFile.modes,
    size: initialCanvasSize,
  });
  window.addEventListener('resize', () => {
    const size = getCanvasRenderSize();
    visualEngine.resize(size.width, size.height);
  }, { passive: true });
  const inputLayer = new InputLayer({ target: refs.canvas });
  inputLayer.attach();

  // Dismiss the "HOLD TO PLAY" hint the moment the user starts actually
  // dragging on the canvas. Both pointerdown and pointermove are listened to
  // so the hint clears whether the user lifts and re-touches after the splash
  // (pointerdown) or keeps the finger down through the splash dismissal and
  // continues dragging onto the canvas (pointermove).
  const dismissHoldHint = (): void => {
    document.body.classList.add('user-held');
    refs.canvas.removeEventListener('pointerdown', dismissHoldHint);
    refs.canvas.removeEventListener('pointermove', dismissHoldHint);
  };
  refs.canvas.addEventListener('pointerdown', dismissHoldHint, { passive: true });
  refs.canvas.addEventListener('pointermove', dismissHoldHint, { passive: true });

  // 3. Tone provides our AudioContext. Suspended on iOS until Tone.start() inside
  //    the first user gesture — SessionManager.handleTap drives that.
  const audioContext = Tone.getContext().rawContext as unknown as AudioContext;

  // 4. UX brain.
  const machineMapper = new MachineMapper({
    seed: Math.floor(Date.now() % 100000),
    idleEvolveMs: IDLE_EVOLVE_MS_EFFECTIVE,
    barMs: (60_000 / activeMode.tempo) * 4,
  });

  // Audio unlock. CRITICAL iOS ordering: all unlock side effects must START
  // synchronously inside the first user gesture, BEFORE any await. iOS does
  // not expose a dependable "audio is audible" readiness signal here, so the
  // enabling tap starts the unlock work and enters the game once silent.mp3
  // accepts playback.
  let audioUnlockPromise: Promise<void> | null = null;
  const unlockAudio = E2E
    ? // E2E test environment: headless Chromium AudioContext.resume() hangs
      // without an audio device. Bypass the real unmute and let SessionManager
      // transition to PLAYING immediately. Audio is verified by unit tests.
      async () => {}
    : () => {
        if (audioUnlockPromise) return audioUnlockPromise;

        const silentPromise = unmuteIOSAudio(refs.silentAudio);
        const rawCtx = (Tone.getContext() as unknown as { rawContext: AudioContext }).rawContext;

        try {
          const buffer = rawCtx.createBuffer(1, 1, 22050);
          const source = rawCtx.createBufferSource();
          source.buffer = buffer;
          source.connect(rawCtx.destination);
          source.start(0);
        } catch (err) {
          console.warn('buffer-source audio unlock failed', err);
        }

        void rawCtx.resume().catch((err) => console.warn('audio context resume failed', err));
        void Tone.start().catch((err) => console.warn('tone start failed', err));

        audioUnlockPromise = silentPromise.catch((err) => {
          audioUnlockPromise = null;
          throw err;
        });
        return audioUnlockPromise;
      };

  const session = new SessionManager({
    audioContext,
    unlockAudio,
    now: () => performance.now(),
  });

  // 5. State change → reflect to DOM. Registered before media loads so the
  //    visible splash is live immediately and slow/failed media cannot eat the
  //    first tap.
  session.on({
    onStateChange: (state, prev) => {
      reflectState(state, refs);
      if (state !== 'PLAYING') audioEngine.setInstrumentGate(false);
      if (DEBUG) console.log(`[state] ${prev} → ${state}`);
    },
  });
  reflectState(session.state, refs); // initial sync (LOADING shows splash)

  // 6. Tap-to-start.
  let bgLoadStarted = false;
  let started = false;
  let audioMode0Ready = false;

  function preloadRemainingVideos(): void {
    if (bgLoadStarted) return;
    bgLoadStarted = true;
    // Background-preload the videos for every mode beyond mode 0. Single-mode
    // builds skip this entirely. Audio loads on demand from the swipe-driven
    // modeChange handler (AudioEngine.loadMode disposes the active bedPlayer).
    for (let i = 1; i < modesFile.modes.length; i++) {
      const idx = i as 0 | 1 | 2;
      void visualEngine
        .loadMode(idx)
        .then(() => visualEngine.playLoadedVideos())
        .catch((err) => console.warn(`preload visual ${idx}`, err));
    }
  }

  async function beginPlayback(): Promise<void> {
    if (!audioMode0Ready || session.state !== 'AWAITING_TAP') return;
    if (started) return;
    started = true;

    // session.handleTap() starts unlockAudio synchronously; audioEngine.start()
    // is also kicked off before the first await so Tone.Transport.start and
    // bedPlayer.start happen on the same gesture stack when assets are ready.
    const tapPromise = session.handleTap();
    void visualEngine.playLoadedVideos();
    void audioEngine.start().catch((err) => console.warn('audio start failed', err));

    try {
      await tapPromise; // AWAITING_TAP -> PLAYING
      preloadRemainingVideos();
    } catch (err) {
      started = false;
      console.error('handleTap failed', err);
      setSplashStatus(refs, 'AUDIO UNAVAILABLE', 1, false, 'Audio unavailable');
    }
  }

  refs.splash.addEventListener('pointerdown', () => {
    void beginPlayback();
  });

  // 7. Resume from INTERRUPTED with any pointerdown. iOS can also leave the
  // AudioContext suspended while the app is already PLAYING without firing a
  // statechange; in that case the next gesture must retry resume/Tone.start().
  document.addEventListener('pointerdown', () => {
    const acState = audioContext.state as string;
    if (session.state === 'INTERRUPTED' || (session.state === 'PLAYING' && acState !== 'running')) {
      const tapPromise = session.handleTap();
      void audioEngine.start().catch((err) => console.warn('audio restart failed', err));
      void tapPromise.catch((err) => console.error(err));
    }
  });

  // 8. Load mode-0 media in the background. Audio is allowed to arrive late:
  //    AudioEngine.start() records wantsPlayback, so the bed starts when the
  //    player finishes loading. Video is fully best-effort; failed/slow video
  //    leaves the shader black until a retry succeeds.
  async function loadAudioMode0(): Promise<void> {
    try {
      setSplashStatus(refs, 'LOADING AUDIO', 0.48, true, 'Loading audio');
      await audioEngine.loadMode(0);
      audioEngine.setMode(0);
      audioMode0Ready = true;
      setSplashStatus(refs, 'TAP TO PLAY', 1, false, 'Tap to play');
      if (session.state === 'LOADING') {
        session.markReady();
      }
      if (started) void audioEngine.start().catch((err) => console.warn('late audio start', err));
    } catch (err) {
      console.warn('mode-0 audio load failed; retrying in 3s', err);
      setSplashStatus(refs, 'LOADING AUDIO', 0.28, true, 'Loading audio');
      setTimeout(() => void loadAudioMode0(), 3000);
    }
  }

  async function loadVideoMode0(): Promise<void> {
    try {
      await visualEngine.loadMode(0);
      visualEngine.setMode(0);
      void visualEngine.playLoadedVideos().catch((err) => console.warn('boot video play', err));
    } catch (err) {
      console.warn('mode-0 video load failed; retrying in 3s', err);
      setTimeout(() => void loadVideoMode0(), 3000);
    }
  }

  void loadAudioMode0();
  void loadVideoMode0();

  // 9. The per-frame rAF loop (spec §5).
  let prevOutput: MachineMapperOutput | null = null;
  let lastIdleResetAtMs = performance.now();

  // Visuals-only render used before the tap (AWAITING_TAP) and during
  // INTERRUPTED: the video shows through the feedback shader with neutral
  // params and no audio. 0.5 is the shader's no-op center for zoom + rotation.
  const NEUTRAL_VISUAL: VisualParams = {
    feedback: 0,
    zoom: 0.5,
    chroma: 0,
    rotation: 0.5,
    flowX: 0,
    flowY: 0,
    warp: 0,
    blur: 0,
    twist: 0,
  };
  const ZERO_LEVELS: AudioLevels = { bass: 0, mid: 0, treble: 0, rms: 0 };
  let lastAudioLevels: AudioLevels = ZERO_LEVELS;

  function frame(nowMs: number): void {
    let activityThisFrame = false;

    if (session.state === 'PLAYING') {
      const gesture = inputLayer.flush(nowMs);
      const instrumentGateOpen = shouldOpenInstrumentGate(gesture);
      audioEngine.setInstrumentGate(instrumentGateOpen);
      activityThisFrame = gestureHadActivity(gesture);
      if (activityThisFrame) lastIdleResetAtMs = nowMs;
      const idleMs = nowMs - lastIdleResetAtMs;

      const out = machineMapper.tick(gesture, activeMode, idleMs, nowMs, prevOutput);

      audioEngine.apply(out.audio);
      const levels = audioEngine.getLevel();
      lastAudioLevels = levels;
      visualEngine.apply(out.visual, levels, out.touchStamps);
      visualEngine.render();

      for (const ev of out.events) {
        if (ev.kind === 'inject') {
          audioEngine.injectOneshot(ev.index);
        } else if (ev.kind === 'modeChange') {
          // Single-mode builds: ignore swipe-driven mode-changes. MachineMapper
          // still emits them (it doesn't know about mode count) but there's
          // nothing to switch to.
          if (ev.toIndex >= modesFile.modes.length) continue;
          activeModeIndex = ev.toIndex;
          activeMode = modesFile.modes[ev.toIndex];
          visualEngine.setMode(ev.toIndex); // video texture swap — cheap

          // Audio: on-demand load + restart the bed loop. AudioEngine.loadMode
          // disposes the active bedPlayer to make room for the new mode's, so
          // we can't preload other modes in the background — we just pay the
          // load cost on the swipe. After loadMode completes, audioEngine.start
          // is idempotent and will .start() the new bedPlayer.
          void (async () => {
            try {
              await audioEngine.loadMode(ev.toIndex);
              audioEngine.setMode(ev.toIndex);
              await audioEngine.start();
            } catch (err) {
              console.warn('mode-change audio load failed', err);
            }
          })();

          // eslint-disable-next-line no-console
          console.log(`[mode] → ${activeModeIndex} (${activeMode.name})`);
        } else if (ev.kind === 'haptic') {
          fireHaptic(refs, ev.flavor);
        }
      }
      prevOutput = out;
    } else {
      // Pre-tap (AWAITING_TAP) or INTERRUPTED: keep the footage on screen so
      // the experience "starts with the media." Render the video through the
      // feedback shader with neutral params + silence — no gesture mapping, no
      // audio side effects.
      audioEngine.setInstrumentGate(false);
      visualEngine.apply(NEUTRAL_VISUAL, ZERO_LEVELS, []);
      lastAudioLevels = ZERO_LEVELS;
      visualEngine.render();
    }

    session.tick(nowMs, activityThisFrame);

    // Debug HUD — refresh ~4× per second to keep it cheap.
    frameCount++;
    if (hud && nowMs - lastHudAtMs > 250) {
      lastHudAtMs = nowMs;
      // Peek at VisualEngine's videoSources via the public method we added;
      // for raw video state we look at the actual element.
      const peek = (visualEngine as unknown as {
        videoSources?: Map<number, { video: HTMLVideoElement; texture: unknown }>;
      }).videoSources;
      const vs0 = peek?.get(activeModeIndex);
      const v = vs0?.video;
      const audioCtxState = (Tone.getContext().rawContext as AudioContext | undefined)?.state ?? '?';
      // Reach into AudioEngine for diagnostic peeks. Private fields accessed
      // explicitly via a cast — debug-only, never depended on by production paths.
      const ae = audioEngine as unknown as {
        bedPlayer?: {
          loaded?: boolean;
          state?: string;
          playbackRate?: number | { value?: number };
        };
        bank?: { size?: number };
        instrumentGate?: { gain?: { value?: number } };
      };
      const bed = ae.bedPlayer;
      const bedRate = typeof bed?.playbackRate === 'number'
        ? bed.playbackRate
        : bed?.playbackRate?.value;
      const audioGate = ae.instrumentGate?.gain?.value;
      const dest = (Tone.getDestination() as unknown as { volume?: { value?: number } }).volume?.value;
      hud.textContent = [
        `state:      ${session.state}`,
        `mode:       ${activeModeIndex} (${activeMode.name})`,
        `frame:      ${frameCount}`,
        `canvas:     ${refs.canvas.width}×${refs.canvas.height}`,
        `audio.ctx:  ${audioCtxState}`,
        `audio.gate: ${audioGate?.toFixed?.(3) ?? '?'}`,
        `dest.vol:   ${dest === undefined ? '?' : dest.toFixed(1) + ' dB'}`,
        `bed.loaded: ${bed?.loaded ?? '?'}`,
        `bed.state:  ${bed?.state ?? '?'}`,
        `bed.rate:   ${bedRate?.toFixed?.(3) ?? '?'}`,
        `bank.size:  ${ae.bank?.size ?? '?'}`,
        `level.rms:  ${lastAudioLevels.rms.toFixed(3)}`,
        `level.b/m/t:${lastAudioLevels.bass.toFixed(3)} / ${lastAudioLevels.mid.toFixed(3)} / ${lastAudioLevels.treble.toFixed(3)}`,
        `video[${activeModeIndex}]:`,
        `  el?       ${v ? 'yes' : 'NO'}`,
        v && `  src       ${v.currentSrc || v.src}`,
        v && `  readyS    ${v.readyState} (4=HAVE_ENOUGH)`,
        v && `  paused    ${v.paused}`,
        v && `  currT     ${v.currentTime.toFixed(2)}s / ${v.duration.toFixed(2)}s`,
        v && `  size      ${v.videoWidth}×${v.videoHeight}`,
        `prev.feedback: ${prevOutput ? prevOutput.visual.feedback.toFixed(3) : '∅'}`,
        `prev.events:   ${prevOutput ? prevOutput.events.length : '∅'}`,
      ].filter(Boolean).join('\n');
    }

    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);
}

boot().catch((err) => {
  console.error('boot failed', err);
  document.body.innerHTML = `<div style="position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:#000;color:#f88;font-family:system-ui;padding:2rem;text-align:center;"><div><h1>boot failed</h1><pre style="white-space:pre-wrap;font-size:0.8rem;opacity:0.7;">${String((err as Error).message ?? err)}</pre></div></div>`;
});
